import { usePreventionParcours } from '@/parcours/composables/usePreventionParcours';
import TextArea from '@/molecules/TextArea.vue';
import defaultValidators from '@/helpers/default-validators';
import SpecialistSelector from '@/parcours/components/specialistSelector.vue';
import { withGlobalFlow } from '@/parcours/flowGlobalSteps';
import ListInfo from '@/parcours/flows/shared/listInfo.vue';
import { MODAL_CONTENT_MELANOMA } from '@/parcours/flows/melanoma/modalContent/modalContentMelanoma';

const { update, getById } = usePreventionParcours('/cms/coordinator-patient-melanoma-infos');

export const melanomaFlow = withGlobalFlow({
  id: 'melanomaFlow',

  basePath: '/melanomaFlow/flow',

  exitRoute: '/melanomaFlow',

  async onInit({ route }) {
    const id = route.params.reservationId;
    const data = await getById(id);
    return { ...data };
  },

  steps: {
    noModifiableRiskFactorsListMen: {
      type: 'component',
      component: ListInfo,
      title: 'Facteurs non-modifiables',
      props: (data: any) => ({
        data,
        config: [
          {
            key: 'family_history',
            label: 'Antécédents personnels ou familiaux de cancers de la peau',
            type: 'tri-boolean',
            section: 'Antécédents',
          },
          {
            key: 'during_childhood_suffer_sunburn',
            label: 'Coups de soleil avec brûlures au second degré (érythème + cloque)',
            type: 'boolean',
          },
          {
            key: 'trouble_tanning_or_more_prone_to_sunburn',
            label: 'Bronzage difficile / coups de soleil fréquents',
            type: 'boolean',
            section: 'Caractéristiques',
          },
          {
            key: 'have_many_moles',
            label:
              'Présence de grains de beauté (≥ 40) ou des grains de beauté larges (> 5mm) et irréguliers',
            type: 'boolean',
          },
          {
            key: 'frequently_exposed_to_uv',
            label: 'Exposition aux UV artificiels',
            type: 'boolean',
            section: 'Exposition',
          },
          {
            key: 'job_exposes_to_risk_factors',
            label:
              'Travail exposant à : UV, arsenic, hydrocarbures aromatiques polycycliques (retrouvés dans le charbon et le pétrole)',
            type: 'boolean',
          },
          {
            key: 'suffering_from_immunosuppression',
            label: 'Personne immunodéprimée',
            type: 'boolean',
            section: 'Information',
          },
        ],
      }),
    },
    preventionAndSensibilisation: {
      type: 'component',
      component: ListInfo,
      title: 'Prévention et sensibilisation',
      props: (data: any) => ({
        data,
        config: [
          {
            label: 'Retrouvez toutes les informations sur le mélanome :',
            value: '',
            key: 'description',
          },
        ],
        actions: [
          {
            label: 'Facteurs de risque',
            onClick: async ({ modal }: any) => {
              await modal.openModal({
                title: 'Facteurs de risque',
                content: MODAL_CONTENT_MELANOMA.RISK,
              });
            },
          },
          {
            label: 'Méthode ABCDE',
            onClick: async ({ modal }: any) => {
              await modal.openModal({
                title: 'Méthode ABCDE',
                content: MODAL_CONTENT_MELANOMA.ABCD_METHOD,
              });
            },
          },
          {
            label: 'Diagnostic et traitements',
            onClick: async ({ modal }: any) => {
              await modal.openModal({
                title: 'Diagnostic et traitements',
                content: MODAL_CONTENT_MELANOMA.TREATMENT,
              });
            },
          },
          {
            label: 'Auto-examen de dépistage',
            onClick: async ({ modal }: any) => {
              await modal.openModal({
                title: 'Auto-examen de dépistage',
                content: MODAL_CONTENT_MELANOMA.EXAMEN,
              });
            },
          },
          {
            label: 'Conseils hygiéno-diététiques',
            onClick: async ({ modal }: any) => {
              await modal.openModal({
                title: 'Conseils hygiéno-diététiques',
                content: MODAL_CONTENT_MELANOMA.HYGIENE,
              });
            },
          },
          {
            label: 'Conseils activité physique',
            onClick: async ({ modal }: any) => {
              await modal.openModal({
                title: 'Conseils activité physique',
                content: MODAL_CONTENT_MELANOMA.PHYSIQUE,
              });
            },
          },
        ],
      }),
    },
    recommandation: {
      id: 'other_specialists',
      type: 'component',
      title: 'Recommandations',
      description:
        "Je recommande au patient d'aller consulter le(s) professionnel(s) de santé suivant(s):",
      component: SpecialistSelector,
      buttonNextTitle: 'Valider',
      props: {
        specialists: [
          { name: 'Dermatologue', value: 'Dermatologue' },
          { name: 'Médecin traitant', value: 'Médecin traitant' },
          { name: 'Autre', value: 'Autre' },
        ],
      },
    },
    lastComment: {
      type: 'form',
      title: 'Un dernier commentaire ?',
      description: 'Autre commentaire ou recommandation pour le patient',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'other_recommendations',
            type: 'textarea',
            placeholder: 'Votre commentaire ...',
            as: TextArea,
            rules: defaultValidators.textOpt,
          },
        ],
      },
    },
  },

  transitions: {
    noModifiableRiskFactorsListMen: { next: 'preventionAndSensibilisation' },
    preventionAndSensibilisation: { next: 'recommandation' },
    recommandation: { next: 'lastComment' },
    lastComment: { submit: true },
  },

  async onSubmit(data) {
    await update(data?.id, data);
  },
});
